import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { faqs, brandMilestones, trustBadges } from '@/mocks/faqs';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9q1b3me3oq4jqumtf50';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('phone_alt');

    setFormStatus('loading');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, value as string);
      });

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      if (response.ok && parsed && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed as { meta?: { message?: string; detail?: string }; message?: string } | null);
        const msg = serverMsg?.meta?.message || serverMsg?.message || serverMsg?.meta?.detail || responseText || '提交失败，请稍后再试';
        if (typeof msg === 'string' && (msg.includes('spam') || msg.includes('form data is spam'))) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(typeof msg === 'string' ? msg : '提交失败，请稍后再试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const aboutPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于爱液 - 品牌故事与专业私密健康护理',
    description: '了解爱液品牌创立历程、国际权威认证、品牌信誉及联系方式。爱液致力于为每一位女性提供安全专业的私密健康护理产品。',
    url: 'https://www.aiye-official.com/about',
    about: {
      '@type': 'Organization',
      name: '爱液',
      description: '爱液品牌官方，专业私密健康护理品牌，产品通过ISO 13485、欧盟CE、SGS等多项国际权威认证'
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };

  const combinedSchema = {
    '@context': 'https://schema.org',
    '@graph': [aboutPageSchema, faqSchema]
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 爱液官网品牌故事与专业私密健康护理"
        description="了解爱液品牌创立历程、国际权威认证、品牌信誉及联系方式。爱液——科学私护，温柔以待，为每一位女性提供安全专业的私密健康护理产品。"
        keywords="爱液品牌官方旗舰,爱液官网品牌故事,爱液官网联系方式,私密健康护理品牌"
        canonicalUrl="/about"
        structuredData={combinedSchema}
      />

      <Navbar />

      <main className="pt-16 md:pt-18">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '关于我们' }
          ]}
        />

        {/* Page Header */}
        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 text-center">
            <h1 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-900 mb-3">
              关于爱液
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              科学私护，温柔以待 —— 了解爱液品牌背后的故事与承诺
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col lg:flex-row items-center gap-8 md:gap-12">
                <div className="w-full lg:w-1/2">
                  <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-background-100">
                    <img
                      src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20brand%20photography%20of%20wellness%20and%20selfcare%20products%20on%20soft%20silk%20fabric%20with%20warm%20natural%20lighting%2C%20clean%20aesthetic%2C%20cream%20and%20coral%20tones%2C%20luxury%20feminine%20brand%20identity%2C%20premium%20lifestyle%20photography%2C%20serene%20and%20empowering%20composition&width=800&height=600&seq=aiye-about-brand-01&orientation=landscape"
                      alt="爱液品牌形象 - 科学私护温柔以待"
                      title="爱液品牌官方旗舰 - 专业私密健康护理"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="w-full lg:w-1/2">
                  <h2 className="text-xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
                    品牌故事
                  </h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    爱液品牌创立于2018年，源于创始团队的一个朴素信念：每一位女性都值得拥有安全、专业、有尊严的私密健康护理体验。
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    我们发现市场上缺乏一款真正从女性需求出发、兼顾安全性与功效性的私密护理产品。于是，我们组建了一支由皮肤科专家、配方工程师和女性健康顾问组成的研发团队，历经两年研发和数百次配方调试，推出了第一款爱液产品。
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    七年以来，爱液始终坚持"科学私护，温柔以待"的品牌理念，从单一产品发展为覆盖水基、硅基、天然植萃的全系列产品矩阵，累计服务超过200万消费者。我们将私密护理提升到与面部护肤同等重要的位置，让每一位女性都能自信、自在地关爱自己。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Brand Milestones */}
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-3xl font-heading font-semibold text-foreground-900 text-center mb-10 md:mb-14">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-secondary-200 md:-translate-x-px"></div>
                <div className="space-y-0">
                  {brandMilestones.map((milestone, index) => (
                    <div
                      key={milestone.year}
                      className={`relative flex flex-col md:flex-row gap-6 md:gap-12 pb-10 md:pb-14 last:pb-0 ${
                        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className="md:w-1/2 pl-12 md:pl-0 md:px-8">
                        <div className="bg-background-50 rounded-2xl border border-background-200 p-5 md:p-6">
                          <span className="inline-block px-3 py-1 bg-primary-50 text-primary-600 text-xs font-semibold rounded-full mb-3">
                            {milestone.year}
                          </span>
                          <h3 className="text-base font-semibold text-foreground-900 mb-2">
                            {milestone.title}
                          </h3>
                          <p className="text-sm text-foreground-600 leading-relaxed">
                            {milestone.description}
                          </p>
                        </div>
                      </div>
                      <div className="absolute left-4 md:left-1/2 top-0 -translate-x-1/2 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50 hidden md:block"></div>
                      <div className="absolute left-4 top-0 -translate-x-1/2 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50 md:hidden"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center mb-10 md:mb-14">
              <h2 className="text-xl md:text-3xl font-heading font-semibold text-foreground-900 mb-3">
                品牌信誉
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                多项国际权威认证，200万+用户信赖之选
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 max-w-4xl mx-auto">
              {trustBadges.map((badge) => (
                <div key={badge.title} className="bg-background-100 rounded-2xl border border-background-200 p-5 md:p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                    <i className={`${badge.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1.5">{badge.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{badge.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8 md:mb-10">
                <h2 className="text-xl md:text-3xl font-heading font-semibold text-foreground-900 mb-3">
                  联系我们
                </h2>
                <p className="text-sm md:text-base text-foreground-600">
                  如有任何产品问题或合作咨询，欢迎留言，我们将在24小时内回复
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-background-50 rounded-2xl border border-background-200 p-6 md:p-8 space-y-5"
              >
                <div className="honeypot-field">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact_name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact_name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact_email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact_email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact_subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    主题 <span className="text-primary-500">*</span>
                  </label>
                  <select
                    id="contact_subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all appearance-none cursor-pointer"
                    defaultValue=""
                  >
                    <option value="" disabled>请选择咨询主题</option>
                    <option value="product">产品咨询</option>
                    <option value="order">订单问题</option>
                    <option value="cooperation">商务合作</option>
                    <option value="feedback">意见反馈</option>
                    <option value="other">其他问题</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="contact_message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact_message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all resize-none"
                    placeholder="请详细描述您的问题或需求（限500字以内）"
                  ></textarea>
                  <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    {formStatus === 'loading' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-base"></i>
                        提交中...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line text-base"></i>
                        发送留言
                      </>
                    )}
                  </button>
                </div>

                {formStatus === 'success' && (
                  <div className="p-4 bg-accent-50 border border-accent-200 rounded-lg text-sm text-accent-700 flex items-center gap-2">
                    <i className="ri-checkbox-circle-line text-base"></i>
                    留言已成功提交！我们将在24小时内通过邮件回复您，请留意查收。
                  </div>
                )}

                {formStatus === 'error' && formError && (
                  <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg text-sm text-primary-700 flex items-center gap-2">
                    <i className="ri-error-warning-line text-base"></i>
                    {formError}
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl md:text-3xl font-heading font-semibold text-foreground-900 text-center mb-10 md:mb-14">
                常见问题
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <details key={index} className="group bg-background-100 rounded-2xl border border-background-200 overflow-hidden">
                    <summary className="flex items-center justify-between p-5 md:p-6 cursor-pointer list-none">
                      <h4 className="text-sm font-semibold text-foreground-900 pr-4">
                        {faq.question}
                      </h4>
                      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 group-open:bg-primary-100 group-open:text-primary-500 flex-shrink-0 transition-colors">
                        <i className="ri-add-line text-sm group-open:hidden"></i>
                        <i className="ri-subtract-line text-sm hidden group-open:block"></i>
                      </div>
                    </summary>
                    <div className="px-5 md:px-6 pb-5 md:pb-6">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}